import { useCallback, useEffect, useRef } from 'react';
import {
  useLocation,
  useNavigate,
  type NavigateOptions,
  type To,
} from 'react-router-dom';

/** Marks the duplicate history entry this hook pushes while it guards. */
const SENTINEL = 'leaveGuard';

type HistoryState = Record<string, unknown> | null;

const onSentinel = () =>
  (window.history.state as HistoryState)?.[SENTINEL] === true;

const pageUrl = () =>
  window.location.pathname + window.location.search + window.location.hash;

/** The router's own keys only, so another layer's markers are not copied. */
const routerState = () => {
  const state = window.history.state as HistoryState;
  return { usr: state?.usr, key: state?.key, idx: state?.idx };
};

const sentinelState = () => ({ ...routerState(), [SENTINEL]: true });

/** Opt a link out when its own click handler already asks about edits. */
export const LEAVE_GUARD_SKIP = { 'data-leave-guard': 'skip' } as const;
/** A guarded link that should replace history, as its `replace` prop does. */
export const LEAVE_GUARD_REPLACE = { 'data-leave-guard': 'replace' } as const;

// One guarding page at a time: the most recent to become active wins.
let askFirst: ((go: () => void) => void) | null = null;

/**
 * For navigation that is not a link, such as a sidebar button. Asks first and
 * returns true when a page is guarding; otherwise the caller navigates.
 */
export function askBeforeLeaving(go: () => void): boolean {
  if (!askFirst) return false;
  askFirst(go);
  return true;
}

interface LeaveGuardOptions {
  /** Whether leaving should be asked about, e.g. while edits are unsaved. */
  active: boolean;
  /** Ask the user; call `leave` to go where they were going. */
  onLeave: (leave: () => void) => void;
}

/**
 * Asks before browser Back, an in-app link or a sidebar button leaves the page.
 *
 * `useBlocker` needs a data router and this app uses `<BrowserRouter>`, so
 * Back is caught with a duplicate history entry pushed while active (as in
 * useMobileBackClose), and link clicks with a capture-phase listener. Every
 * navigation the page makes itself must go through the returned `navigate`,
 * which first steps off that entry so a replace does not leave it behind.
 */
export function useLeaveGuard({ active, onLeave }: LeaveGuardOptions) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeRef = useRef(active);
  const onLeaveRef = useRef(onLeave);
  const pathnameRef = useRef(location.pathname);
  // The page's URL when the entry was pushed, so a jump further back than
  // the entry (the history menu) is let go rather than re-armed elsewhere.
  const armedAt = useRef<string | null>(null);
  // A step off the entry in flight, resolved by the popstate it causes.
  const stepping = useRef<{
    promise: Promise<void>;
    resolve: () => void;
  } | null>(null);

  useEffect(() => {
    onLeaveRef.current = onLeave;
    pathnameRef.current = location.pathname;
  });

  /** Leave the duplicate entry, if it is the current one. */
  const stepOff = useCallback((): Promise<void> => {
    if (stepping.current) return stepping.current.promise;
    if (!onSentinel()) return Promise.resolve();
    let resolve = () => {};
    const promise = new Promise<void>(done => {
      resolve = done;
    });
    stepping.current = { promise, resolve };
    window.history.back();
    return promise;
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const step = stepping.current;
      if (step) {
        stepping.current = null;
        step.resolve();
        return;
      }
      if (onSentinel()) {
        // Forward onto an entry left from an earlier step off. Adopt it while
        // guarding; otherwise make it plain, so the next edit arms afresh.
        if (activeRef.current) armedAt.current = pageUrl();
        else window.history.replaceState(routerState(), '');
        return;
      }
      if (!activeRef.current || pageUrl() !== armedAt.current) return;
      // Back left the entry: put it back, and go only once the user agrees.
      window.history.pushState(sentinelState(), '');
      onLeaveRef.current(() => {
        void stepOff().then(() => window.history.back());
      });
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      // So an arm queued behind a step in flight does nothing once gone.
      activeRef.current = false;
    };
  }, [stepOff]);

  useEffect(() => {
    activeRef.current = active;
    if (!active) {
      // Idempotent while a step is in flight, so StrictMode's re-run is safe.
      void stepOff();
      return;
    }
    const arm = () => {
      if (!activeRef.current) return;
      armedAt.current = pageUrl();
      if (!onSentinel()) window.history.pushState(sentinelState(), '');
    };
    if (stepping.current) void stepping.current.promise.then(arm);
    else arm();
    const ask = (go: () => void) =>
      onLeaveRef.current(() => {
        void stepOff().then(go);
      });
    askFirst = ask;
    return () => {
      if (askFirst === ask) askFirst = null;
    };
  }, [active, stepOff]);

  useEffect(() => {
    if (!active) return;
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor =
        event.target instanceof Element
          ? event.target.closest('a[href]')
          : null;
      if (
        !(anchor instanceof HTMLAnchorElement) ||
        anchor.dataset.leaveGuard === 'skip' ||
        (anchor.target && anchor.target !== '_self') ||
        anchor.hasAttribute('download')
      )
        return;
      const url = new URL(anchor.href, window.location.href);
      if (
        !/^https?:$/.test(url.protocol) ||
        url.origin !== window.location.origin ||
        url.pathname === pathnameRef.current
      )
        return;
      event.preventDefault();
      const to = url.pathname + url.search + url.hash;
      // Router state on the link is not carried; a plain push unless marked.
      const replace = anchor.dataset.leaveGuard === 'replace';
      onLeaveRef.current(() => {
        void stepOff().then(() => navigate(to, { replace }));
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [active, navigate, stepOff]);

  // Synchronous when there is no entry to leave, so it batches like a plain
  // navigate inside the click that asked for it.
  const guardedNavigate = useCallback(
    (to: To, options?: NavigateOptions): Promise<void> => {
      if (!stepping.current && !onSentinel()) {
        navigate(to, options);
        return Promise.resolve();
      }
      return stepOff().then(() => navigate(to, options));
    },
    [navigate, stepOff]
  );

  return { navigate: guardedNavigate };
}
